import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../lib/firebase.js';
import BranchBadge, { StatusSelect } from '../components/BranchBadge.jsx';
import { BRANCHES } from '../lib/branches.js';
import SocHeader from '../components/SocHeader.jsx';
import Footer from '../components/Footer.jsx';

async function ensureUserDoc(user, displayName, status, branch) {
  await setDoc(
    doc(db, 'users', user.uid),
    {
      uid: user.uid,
      email: user.email,
      displayName: displayName || user.displayName || '',
      photoURL: user.photoURL || '',
      ...(status ? { status } : {}),
      ...(branch ? { branch } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

export default function SignUp() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('');
  const [branch, setBranch] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (name.trim()) {
        await updateProfile(cred.user, { displayName: name.trim() });
      }
      await ensureUserDoc(cred.user, name.trim(), status, branch);
      navigate('/profile', { replace: true });
    } catch (error) {
      setErr(error.message.replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    setErr('');
    setLoading(true);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      await ensureUserDoc(cred.user, '', status, branch);
      navigate('/profile', { replace: true });
    } catch (error) {
      setErr(error.message.replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SocHeader />
      <section className="auth-section" id="main">
        <div className="auth-card">
          <div className="hero-eyebrow">ENLIST</div>
          <h2 className="auth-headline">Create Account</h2>
          <p className="auth-sub">Free forever. Sync progress, flashcards, and exam history across devices.</p>

          {err && <div className="form-msg error">SIGNUP FAIL — {err}</div>}

          <button type="button" className="btn btn-ghost btn-block" onClick={onGoogle} disabled={loading}>
            Continue with Google
          </button>

          <div className="auth-divider"><span>OR</span></div>

          <form onSubmit={onSubmit} className="contact-form">
            <div className="form-group">
              <label className="form-label" htmlFor="name">Display Name</label>
              <input id="name" type="text" className="form-input" autoComplete="name"
                value={name} onChange={(e) => setName(e.target.value)} placeholder="CALL SIGN" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="email">Email</label>
              <input id="email" type="email" className="form-input" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="OPERATOR@DOMAIN" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <input id="password" type="password" className="form-input" autoComplete="new-password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="MIN 8 CHARS" />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="status-select">Status <span style={{ color: 'var(--bone-faint)', fontWeight: 400 }}>(optional)</span></label>
              <StatusSelect id="status-select" value={status} onChange={setStatus} disabled={loading} />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="branch-select">Branch of Service <span style={{ color: 'var(--bone-faint)', fontWeight: 400 }}>(optional)</span></label>
              <div className="branch-select-row">
                {branch && <BranchBadge branchKey={branch} statusKey={status} size={36} />}
                <select
                  id="branch-select"
                  className="form-input branch-select"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Select Branch --</option>
                  {BRANCHES.map((b) => (
                    <option key={b.key} value={b.key}>{b.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button type="submit" className="btn btn-block" disabled={loading}>
              {loading ? 'PROVISIONING…' : 'Create Account →'}
            </button>
          </form>

          <div className="auth-links">
            <span>Already enlisted?</span>
            <Link to="/signin">Sign In</Link>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
